/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Navigate, Route, RouteObject, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  selectCanUseQuickOrder,
  selectIsCEUser,
  selectIsThisTfo,
} from '../../features/azure-b2c/storage';
import {
  selectRemoteConfig,
  selectShouldShowActivities,
} from '../../services/firebase/remote-config/remoteConfig.slice';

import Home from '../../pages/Home';
import PastProducts from '../../pages/Products';
import SalesAgreements from '../../pages/SalesAgreements';
import SalesAgreement from '../../features/sales-agreements/SalesAgreement';
import Cart from '../../pages/Cart';
import { CheckoutSuccess } from '../../pages/CheckoutSuccess';
import Orders from '../../pages/Orders';
import OrderDetails from '../../features/orders/OrderDetails';
import { RequestAQuoteSuccess } from '../../pages/RequestAQuoteSuccess';
import { DrawdownSuccess } from '../../pages/DrawdownSuccessV2';
import ProductDetails from '../../features/products/pages/ProductDetails';
import { EnquiryRequestSent } from '../../pages/EnquiryRequestSent';
import { ProductNotFound } from '../../features/products/pages/ProductNotFound';
import { SeedOrder } from '../../pages/SeedOrder';
import { SeedOrderSummary } from '../../features/seed-orders/SeedOrderSummary';
import { SeedOrderSuccess } from '../../pages/SeedOrderSuccess';
import QuoteDetails from '../../features/quotes/QuoteDetails';
import Quotes from '../../pages/Quotes';
import { QuoteOrderSubmitted } from '../../pages/QuoteOrderSubmitted';
import { CampaignSuccess } from '../../pages/CampaignSuccess';
import { HOME_ANY_ROUTE, HOME_ROUTE, LOGOUT_CE_USER_ROUTE, LOGOUT_ROUTE } from '../../util/routing';
import { accountRoutes } from '../../features/account/account.routes';
import { ORDERS_ROUTE } from '../../features/orders/orders.routes';
import { PRODUCTS_ROUTE, PRODUCTS_CART_ROUTE } from '../../features/products/products.routes';
import { QUOTES_ROUTE } from '../../features/quotes/quotes.routes';
import { AGREEMENTS_ROUTE } from '../../features/sales-agreements/sales-agreements-routes';
import { SEED_ROUTE } from '../../features/seed-orders/seed.routes';
import Logout from './Logout';
import { useMediaQuery, useTheme } from '@mui/material';
import { activitiesRoutes } from '../../features/activities/activities-routes';
import LogoutCEUser from './LogoutCEUser';

const AuthenticatedRoutes = () => {
  const canUseCart = useSelector(selectCanUseQuickOrder);
  const remoteConfig = useSelector(selectRemoteConfig);
  const shouldShowActivities = useSelector(selectShouldShowActivities);
  const isCEUser = useSelector(selectIsCEUser);
  const isTfo = useSelector(selectIsThisTfo) ?? false;
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Routes>
      <Route path='/'>
        <Route path={LOGOUT_ROUTE} element={<Logout />} />
        <Route path={LOGOUT_CE_USER_ROUTE} element={<LogoutCEUser />} />
        <Route path={HOME_ANY_ROUTE} element={<Home />} />
        <Route path={PRODUCTS_ROUTE} element={<PastProducts />} />
        <Route path='/products/not-found' element={<ProductNotFound />} />
        <Route path='/products/:masterProductId' element={<ProductDetails />} />
        <Route path='/enquiry-request-sent' element={<EnquiryRequestSent />} />
        {canUseCart && <Route path={PRODUCTS_CART_ROUTE} element={<Cart />} />}
        {canUseCart && (
          <Route path='/products/checkout/:confirmationId' element={<CheckoutSuccess />} />
        )}
        <Route
          path={SEED_ROUTE}
          element={remoteConfig.seedEnabled ? <SeedOrder /> : <Navigate to={HOME_ROUTE} />}
        />
        {remoteConfig.seedEnabled && (
          <>
            <Route
              path='/seed/summary'
              element={canUseCart ? <SeedOrderSummary /> : <Navigate to={HOME_ROUTE} />}
            />
            <Route
              path='/seed/success'
              element={canUseCart ? <SeedOrderSuccess /> : <Navigate to={HOME_ROUTE} />}
            />
          </>
        )}

        <Route
          path='/request-quote-success'
          element={
            remoteConfig.quoteEnabled ? <RequestAQuoteSuccess /> : <Navigate to={HOME_ROUTE} />
          }
        />
        <Route
          path={QUOTES_ROUTE}
          element={remoteConfig.quoteEnabled ? <Quotes /> : <Navigate to={HOME_ROUTE} />}
        />
        <Route
          path='/quotes/:quoteId'
          element={remoteConfig.quoteEnabled ? <QuoteDetails /> : <Navigate to={HOME_ROUTE} />}
        />
        <Route
          path='/quote-order-submitted/:confirmationId'
          element={
            remoteConfig.quoteEnabled ? <QuoteOrderSubmitted /> : <Navigate to={HOME_ROUTE} />
          }
        />

        <Route path={AGREEMENTS_ROUTE} element={<SalesAgreements />} />
        <Route path='/agreement/:salesAgreementId' element={<SalesAgreement />} />
        <Route path='/agreement/:salesAgreementId/drawdown' element={<DrawdownSuccess />} />
        <Route path='/agreements/campaign-success' element={<CampaignSuccess />} />

        <Route path={ORDERS_ROUTE} element={<Orders />} />
        <Route path='/order/:orderId' element={<OrderDetails />} />
        <Route path='/' element={<Navigate to={HOME_ROUTE} />} />
        <Route path='/login' element={<Navigate to={HOME_ROUTE} />} />

        {shouldShowActivities &&
          activitiesRoutes(isTfo).map(({ path, element }: RouteObject) => (
            <Route key={path} path={path} element={element} />
          ))}

        {accountRoutes(isCEUser, isMobileSize).map(({ path, element }: RouteObject) => (
          <Route key={path} path={path} element={element} />
        ))}
      </Route>
      <Route path='' element={<Navigate to={HOME_ROUTE} />} />
      <Route path='*' element={<Navigate to={HOME_ROUTE} />} />
    </Routes>
  );
};

export default AuthenticatedRoutes;
